// apps/dashboard/src/lib/api/firm-profile.ts
//
// Client for `/api/admin/firm/*` (Task 15, template-library-catalogue
// spec — see apps/api/src/routes/admin-firm-profile.ts for the exact
// routes this wraps). Mirrors `lib/api/catalogue.ts`'s conventions: typed
// fetchers over `apiClient`, `{ success, data }` envelope unwrapped here so
// callers never see it.
//
// Both routes are ADMIN only server-side.
import { apiClient, ApiClientError } from './client';

export type FirmProfileGroup =
  | 'identity'
  | 'regulatory'
  | 'client_account'
  | 'complaints'
  | 'data_protection'
  | 'retention_limits'
  | 'fees'
  | 'terms_of_business'
  | 'privacy_notice'
  | 'brand';

export type FirmProfileType =
  | 'text'
  | 'block'
  | 'email'
  | 'phone'
  | 'url'
  | 'money'
  | 'percent'
  | 'number'
  | 'days'
  | 'years'
  | 'image'
  | 'palette'
  | 'select';

export interface FirmProfileField {
  key: string;
  label: string;
  group: FirmProfileGroup;
  help: string;
  type: FirmProfileType;
  requiredForLive: boolean;
  libraryToken?: string;
  /** `select` only — the closed set of allowed values (e.g. firm_date_format). */
  options?: readonly string[];
  maxLength?: number;
}

export interface FirmProfileCompleteness {
  required: number;
  filled: number;
}

export interface FirmProfileSchemaResponse {
  schema: FirmProfileField[];
  values: Record<string, string>;
  completeness: FirmProfileCompleteness;
}

export interface FieldError {
  key: string;
  message: string;
}

/** Thrown by `updateFirmProfile` on a 400 — carries the route's per-key
 *  `{ errors: [{ key, message }] }` body so the form can show them inline. */
export class FirmProfileValidationError extends Error {
  constructor(public readonly errors: FieldError[]) {
    super(errors.map((e) => `${e.key}: ${e.message}`).join('; ') || 'Validation failed.');
    this.name = 'FirmProfileValidationError';
  }
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

const PROFILE_SCHEMA_PATH = '/api/admin/firm/profile-schema';
const PROFILE_PATH = '/api/admin/firm/profile';

/** `GET /api/admin/firm/profile-schema`. */
export async function getFirmProfileSchema(): Promise<FirmProfileSchemaResponse> {
  const res = await apiClient.get<ApiResponse<FirmProfileSchemaResponse>>(PROFILE_SCHEMA_PATH);
  return res.data;
}

/**
 * `PUT /api/admin/firm/profile` — `{ values }` should only contain the
 * keys the caller actually changed. Throws `FirmProfileValidationError` on
 * the route's 400 `{ errors }` body so callers can show per-field errors
 * without string-matching a generic message.
 */
/** `requestedKeys`: bank-detail changes filed for a second approver's
 *  sign-off rather than written (four-eyes; the API's re-review N3). */
export interface UpdateFirmProfileResult {
  changedKeys: string[];
  requestedKeys?: string[];
}

export async function updateFirmProfile(values: Record<string, string>): Promise<UpdateFirmProfileResult> {
  try {
    const res = await apiClient.put<ApiResponse<UpdateFirmProfileResult>>(PROFILE_PATH, { values });
    return res.data;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 400) {
      const body = err.body as { errors?: FieldError[] } | undefined;
      if (body?.errors) throw new FirmProfileValidationError(body.errors);
    }
    throw err;
  }
}
